'use client';

import { ArrowRight } from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';

import { MarketingFooter } from '@/components/marketing/MarketingFooter';
import { MarketingNav } from '@/components/marketing/MarketingNav';
import { NewsCover } from '@/components/marketing/NewsCover';
import { useSiteContent } from '@/hooks/useSiteContent';
import { useApi } from '@/hooks/useApi';
import { formatNewsDate, newsHref, type NewsFeed, type NewsListItem } from '@/lib/news';

/** Total pages: page 1 holds 1 featured + 12 grid (13); later pages hold 12. */
function totalPages(count: number): number {
  if (count <= 13) return 1;
  return 1 + Math.ceil((count - 13) / 12);
}

function NewsCard({ item }: { item: NewsListItem }) {
  return (
    <Link
      href={newsHref(item)}
      className="group flex flex-col overflow-hidden rounded-lg border border-isps-outline-soft/50 bg-white shadow-sm !no-underline transition hover:-translate-y-1 hover:shadow-premium"
    >
      <div className="relative h-48 bg-gray-200">
        <NewsCover
          src={item.image_url}
          alt={item.title}
          imgClassName="transition-transform duration-500 group-hover:scale-105"
        />
        {item.hero_tag ? (
          <span className="absolute left-3 top-3 rounded bg-isps-gold-500 px-2.5 py-1 text-xs font-semibold text-isps-dark-900">
            {item.hero_tag}
          </span>
        ) : null}
      </div>
      <div className="flex flex-1 flex-col p-5">
        <p className="text-xs font-medium uppercase tracking-wide text-isps-dark-400">
          {formatNewsDate(item.date)}
          {item.location ? ` · ${item.location}` : ''}
        </p>
        {/* Four lines, matching the landing-page news cards. Broadcast
            headlines are pasted in full, and one ran to six lines and
            stretched its whole row of cards to match. */}
        <h3
          title={item.title}
          className="mt-2 line-clamp-4 text-base font-bold leading-snug text-isps-dark-900 transition group-hover:text-isps-gold-700"
        >
          {item.title}
        </h3>
        <span className="mt-auto inline-flex items-center pt-4 text-sm font-semibold text-isps-gold-700">
          Read more <ArrowRight className="ml-1 h-4 w-4" />
        </span>
      </div>
    </Link>
  );
}

export default function NewsListPage() {
  const site = useSiteContent();
  const [page, setPage] = useState(1);
  const { data, loading } = useApi<NewsFeed>(`/api/news/feed/?page=${page}`);

  const results = data?.results ?? [];
  const count = data?.count ?? 0;
  const pages = totalPages(count);

  // Page 1 -> first item is featured; later pages -> all grid.
  const featured = page === 1 ? results[0] : undefined;
  const grid = page === 1 ? results.slice(1) : results;

  return (
    <div className="min-h-screen bg-isps-surface text-isps-dark-900">
      <MarketingNav nav={site?.nav} socials={site?.socials} />

      {/* Page header band — dark so the transparent nav stays legible */}
      <header className="bg-isps-dark-900 pb-14 pt-36 text-center text-white">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <h1 className="text-3xl sm:text-4xl font-extrabold">News &amp; Updates</h1>
          <div className="mx-auto mt-4 h-1 w-16 bg-isps-gold-500" />
          <p className="mx-auto mt-4 max-w-2xl text-white/70">
            Stay informed about the latest developments from the scheme.
          </p>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-14 lg:px-8">
        {loading && results.length === 0 ? (
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-80 animate-pulse rounded-lg bg-white shadow-sm" />
            ))}
          </div>
        ) : results.length === 0 ? (
          <p className="py-20 text-center text-isps-dark-400">No news articles yet.</p>
        ) : (
          <>
            {featured ? (
              <Link
                href={newsHref(featured)}
                className="group mb-12 grid overflow-hidden rounded-2xl border border-isps-outline-soft/50 bg-white shadow-sm !no-underline transition hover:shadow-premium md:grid-cols-2"
              >
                <div className="relative h-64 bg-gray-200 md:h-full">
                  <NewsCover src={featured.image_url} alt={featured.title} />
                  <span className="absolute left-4 top-4 rounded bg-isps-gold-500 px-3 py-1 text-xs font-bold text-isps-dark-900">
                    {featured.hero_tag || 'FEATURED'}
                  </span>
                </div>
                <div className="flex flex-col justify-center p-8">
                  <p className="text-xs font-medium uppercase tracking-wide text-isps-dark-400">
                    {formatNewsDate(featured.date)}
                    {featured.location ? ` · ${featured.location}` : ''}
                  </p>
                  <h2
                    title={featured.title}
                    className="mt-3 line-clamp-4 text-2xl font-bold leading-tight text-isps-dark-900 transition group-hover:text-isps-gold-700"
                  >
                    {featured.title}
                  </h2>
                  <span className="mt-5 inline-flex items-center font-semibold text-isps-gold-700">
                    Read full story <ArrowRight className="ml-1 h-4 w-4" />
                  </span>
                </div>
              </Link>
            ) : null}

            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {grid.map((item) => (
                <NewsCard key={item.id} item={item} />
              ))}
            </div>

            {pages > 1 ? (
              <div className="mt-14 flex items-center justify-center gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="rounded-md border border-isps-outline-soft px-4 py-2 text-sm font-semibold text-isps-dark-700 transition hover:bg-white disabled:opacity-40"
                >
                  Previous
                </button>
                {Array.from({ length: pages }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setPage(i + 1)}
                    className={`h-9 w-9 rounded-md text-sm font-semibold transition ${
                      page === i + 1
                        ? 'bg-isps-gold-500 text-isps-dark-900'
                        : 'border border-isps-outline-soft text-isps-dark-700 hover:bg-white'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={page >= pages}
                  onClick={() => setPage((p) => Math.min(pages, p + 1))}
                  className="rounded-md border border-isps-outline-soft px-4 py-2 text-sm font-semibold text-isps-dark-700 transition hover:bg-white disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            ) : null}
          </>
        )}
      </main>

      <MarketingFooter links={site?.footer_links} socials={site?.socials} />
    </div>
  );
}
